import React from "react";
import { useTranslation } from "react-i18next";
import styled from "styled-components";
import { Text } from "../../../../components/Text";
import { Flex } from "../../../../components/Flex";

import { kanbanSliceSelectors } from "../../../../redux/kanban/kanban.selector";
import { useAppSelector } from "../../../../redux/store";
import { userSliceSelectors } from "../../../../redux/user/user.selector";
import { standardDate } from "../../../../utils/Date";
import { getTranslatedKanbanColumnName } from "../../KanbanUtils";
import { Button } from "../../../../components/Button";
import { UserRoles } from "../../../../model/user/types/UserRoles";
import { EntityAssignCheckbox } from "../../../../components/EntityAssignCheckbox";
import { useDrawersContext } from "../../../tasks/drawers-context";
import isEmpty from "lodash/isEmpty";
import omit from "lodash/omit";
import { SpinnerFullScreen } from "../../../../components/SpinnerFullScreen";

const StyledButton = styled(Button)`
  height: 2.5rem;
  border-radius: 0.3rem;
  width: 7rem;
`;

export const KanbanCardDrawerInfoTab: React.FC = () => {
  const { t } = useTranslation();

  const { kanbanCard, setKanbanCardUpdateVisible } = useDrawersContext();

  const currentKanban = useAppSelector(
    kanbanSliceSelectors.selectUserCurrentKanban,
  );
  const user = useAppSelector(userSliceSelectors.selectLoggedInUser)!;

  const parentColumn = currentKanban?.columns?.find(
    (col) => col._id === kanbanCard?.parentId,
  );

  const parentEntry = kanbanCard?.entry || (kanbanCard as any)?.entryDoc;
  const cardFinalized = Boolean(kanbanCard?.closedAt);
  const canEditCard =
    !cardFinalized && user?.permissions?.permissions?.KANBAN_CARD?.UPDATE;
  const canSeeAssignees = user.roles.includes(UserRoles.ORGANIZATION);
  const showLoading = isEmpty(omit(kanbanCard, ["_id"]));

  const contactName =
    parentEntry?.fields?.email ||
    parentEntry?.fields?.phone ||
    parentEntry?.fields?.name ||
    parentEntry?._id;

  if (!kanbanCard) {
    return null;
  }

  if (showLoading) {
    /* See: onViewEntity() in utils/Reports.tsx */
    return <SpinnerFullScreen />;
  }

  return (
    <Flex flex={1} flexDirection="column" gap="m">
      <Text>
        <strong>{t("common.id")}: </strong>

        <span>{kanbanCard?._id}</span>
      </Text>

      <Text>
        <strong>{t("deals.drawer.input.deal_name")}: </strong>

        <span>{kanbanCard?.title}</span>
      </Text>

      <Text>
        <strong>{t("common.contact")}: </strong>
        <span>{contactName}</span>
      </Text>

      {canSeeAssignees && kanbanCard?.entry?.assigneeIds?.length ? (
        <EntityAssignCheckbox value={kanbanCard?.entry?.assigneeIds} disabled />
      ) : null}

      {parentColumn?.name ? (
        <Text>
          <strong>{t("common.stage")}: </strong>

          <span>
            {getTranslatedKanbanColumnName(
              parentColumn?.name,
              currentKanban?.template,
            )}
          </span>
        </Text>
      ) : null}

      <Text>
        <strong>{t("common.amount")}: </strong>
        <span>{kanbanCard?.amount || 0}$</span>
      </Text>

      {kanbanCard?.closingDate ? (
        <Text>
          <strong>{t("deals.drawer.input.closing_date")}: </strong>

          <span>{standardDate<string>(kanbanCard.closingDate, user)}</span>
        </Text>
      ) : null}

      {kanbanCard?.note ? (
        <Text>
          <strong>{t("common.note")}: </strong>

          <span>{kanbanCard.note}</span>
        </Text>
      ) : null}

      {kanbanCard?.customFields?.map((field, index) => (
        <Text key={`${field.label}-${field.value}-${index}`}>
          <strong>{field.label}: </strong>

          <span>{field.value}</span>
        </Text>
      ))}

      {kanbanCard?.movingReason ? (
        <Text>
          <strong>{t("deals.darwer.preview.closingReason")}: </strong>
          <span>{kanbanCard.movingReason}</span>
        </Text>
      ) : null}

      {canEditCard ? (
        <StyledButton
          title={t("common.edit")}
          onClick={() => setKanbanCardUpdateVisible(true)}
        />
      ) : null}
    </Flex>
  );
};
